// Helper functions for store access control

import { useQuery } from '@tanstack/react-query';
import axios from 'axios';

 const hasPermissionFlag = (staffData, key) => {
  if (!staffData) return false;
  const flags = staffData.permission_flags && typeof staffData.permission_flags === 'object'
    ? staffData.permission_flags
    : {};
  return !!flags[key];
 };

 const hasEffectivePermission = (staffData, permission) => {
  if (!staffData) return false;
  const permissions = Array.isArray(staffData.permissions) ? staffData.permissions : [];
  return permissions.includes('*') || permissions.includes(permission);
 };

// Fetch user's staff roles for a specific store
export const useStoreStaffRoles = (storeIdOrSlug) => {
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  return useQuery({
    queryKey: ['storeStaff', storeIdOrSlug],
    queryFn: async () => {
      if (!storeIdOrSlug || !token) return null;
      try {
        const response = await axios.get(`/api/store/${encodeURIComponent(storeIdOrSlug)}/staff/me`, {
          params: { _ts: Date.now() },
          __suppress403Toast: true,
          __suppress401Redirect: true,
        });
        return response?.data || null;
      } catch (err) {
        const status = err?.response?.status;
        if (status === 404 || status === 403 || status === 401) {
          return null;
        }
        return null;
      }
    },
    enabled: Boolean(storeIdOrSlug && token),
    retry: false,
  });
};

// Check if user can access store dashboard
export const canAccessStoreDashboard = (staffData) => {
  return hasPermissionFlag(staffData, 'dashboard') || hasEffectivePermission(staffData, 'store.dashboard.access');
};

// Check if user can access store POS
export const canAccessStorePOS = (staffData) => {
  return hasPermissionFlag(staffData, 'pos') || hasEffectivePermission(staffData, 'store.pos.access');
};

// Check if user can view store invoices
export const canViewStoreInvoices = (staffData) => {
  return hasPermissionFlag(staffData, 'invoices') || hasEffectivePermission(staffData, 'store.documents.view');
};

// Check if user can access kitchen display
export const canAccessKitchenDisplay = (staffData) => {
  return hasPermissionFlag(staffData, 'kitchen') || hasEffectivePermission(staffData, 'store.kitchen.access');
};

 export const canAccessStoreDelivery = (staffData) => {
  return hasPermissionFlag(staffData, 'delivery') || hasEffectivePermission(staffData, 'store.delivery.manage');
 };

 export const canManageStoreSettings = (staffData) => {
  return hasPermissionFlag(staffData, 'settings') || hasEffectivePermission(staffData, 'store.settings.manage');
 };

 export const canViewStoreReports = (staffData) => {
  return hasPermissionFlag(staffData, 'reports') || hasEffectivePermission(staffData, 'store.reports.view');
 };

 export const canOpenCloseStoreCash = (staffData) => {
  return hasPermissionFlag(staffData, 'cashOpenClose') || hasEffectivePermission(staffData, 'store.cash.open') || hasEffectivePermission(staffData, 'store.cash.close');
 };

export const canManageStoreDocuments = (staffData) => {
  return hasPermissionFlag(staffData, 'documentsManage') || hasEffectivePermission(staffData, 'store.documents.manage');
};

 export const canManageStoreInventory = (staffData) => {
  return hasPermissionFlag(staffData, 'inventoryManage') || hasEffectivePermission(staffData, 'store.inventory.manage');
 };

export const canAccessStoreProducts = (staffData) => {
  return hasPermissionFlag(staffData, 'products') ||
         hasPermissionFlag(staffData, 'inventory') ||
         hasPermissionFlag(staffData, 'inventoryManage') ||
         hasEffectivePermission(staffData, 'store.products.manage') ||
         hasEffectivePermission(staffData, 'store.inventory.manage');
};

export const canAccessStoreOverview = (staffData) => {
  return hasPermissionFlag(staffData, 'reports') ||
         hasEffectivePermission(staffData, 'store.reports.view');
};

 export const canManageStoreAppointments = (staffData) => {
  return hasPermissionFlag(staffData, 'appointmentsManage') || hasEffectivePermission(staffData, 'store.appointments.manage');
 };

 export const canManageStoreMessaging = (staffData) => {
  return hasPermissionFlag(staffData, 'messagingManage') || hasEffectivePermission(staffData, 'store.messaging.manage');
 };

 export const canViewStoreAccounting = (staffData) => {
  return hasPermissionFlag(staffData, 'accountingView') || hasEffectivePermission(staffData, 'store.accounting.view');
 };

 export const canManageStoreAccounting = (staffData) => {
  return hasPermissionFlag(staffData, 'accountingManage') || hasEffectivePermission(staffData, 'store.accounting.manage');
 };

 export const canManageStoreBanks = (staffData) => {
  return hasPermissionFlag(staffData, 'bankManage') || hasEffectivePermission(staffData, 'store.bank.manage');
 };

 export const canManageStorePayroll = (staffData) => {
  return hasPermissionFlag(staffData, 'payrollManage') || hasEffectivePermission(staffData, 'store.payroll.manage');
 };

// Seller-specific permissions
export const canManageSellerClients = (staffData) => {
  return hasPermissionFlag(staffData, 'sellerClients') || hasEffectivePermission(staffData, 'store.seller.clients.manage');
};

export const canCreateSellerOrders = (staffData) => {
  return hasPermissionFlag(staffData, 'sellerOrders') || hasEffectivePermission(staffData, 'store.seller.orders.create');
};

export const canViewSellerCommissions = (staffData) => {
  return hasPermissionFlag(staffData, 'sellerCommissions') || hasEffectivePermission(staffData, 'store.seller.commissions.view');
};

export const canAccessSellerTab = (staffData) => {
  return canManageSellerClients(staffData) || canCreateSellerOrders(staffData) || canViewSellerCommissions(staffData);
};

// Customers permissions
export const canManageCustomers = (staffData) => {
  return hasPermissionFlag(staffData, 'customersManage') || hasEffectivePermission(staffData, 'store.customers.manage');
};

export const canViewCustomers = (staffData) => {
  return hasPermissionFlag(staffData, 'customersView') || hasEffectivePermission(staffData, 'store.customers.view') || canManageCustomers(staffData);
};

export const canAccessClientsTab = (staffData) => {
  return canViewCustomers(staffData);
};

export const canViewStoreLodging = (staffData) => {
  return hasPermissionFlag(staffData, 'lodging') || hasEffectivePermission(staffData, 'store.lodging.view');
};

export const canAccessTravelPlans = (staffData) => {
  return hasPermissionFlag(staffData, 'travelPlans') || hasEffectivePermission(staffData, 'store.products.manage') || hasEffectivePermission(staffData, 'store.inventory.manage');
};

// Get store slug from store ID
export const getStoreSlugFromId = async (storeId) => {
  if (!storeId) return null;
  try {
    const response = await axios.get(`/api/store/${storeId}/info`);
    const data = response?.data || null;
    return data?.slug || null;
  } catch {
    return null;
  }
};
